import { useEffect, useState } from 'react';
import { Clock, Medal, Trophy, User } from 'lucide-react';
import { RafflePointsIcon } from './RafflePointsIcon';

/**
 * Shared building blocks for the Weekly Raffle leaderboards (Public Server +
 * Aim Training + Admin): countdown to the Monday 00:00 reset, payout badges,
 * and the top-3 podium ("Treppchen").
 */

export interface WeeklyBoardEntry {
    rank: number;
    id?: string | null;
    username: string;
    avatarUrl: string | null;
    elo: number;
    points: number; // raffle points this placement earns
}

export function formatCountdown(ms: number): string {
    if (ms <= 0) return '0m';
    const totalMin = Math.floor(ms / 60000);
    const d = Math.floor(totalMin / 1440);
    const h = Math.floor((totalMin % 1440) / 60);
    const m = totalMin % 60;
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
}

/** Ticks once a minute towards `target`; returns the formatted remainder. */
export function useCountdown(target: string | Date | null | undefined): string {
    const [, setTick] = useState(0);
    useEffect(() => {
        const iv = setInterval(() => setTick(t => t + 1), 30_000);
        return () => clearInterval(iv);
    }, []);
    if (!target) return '—';
    return formatCountdown(new Date(target).getTime() - Date.now());
}

/** "Weekly reset in …" chip, shown next to every weekly leaderboard header. */
export function ResetCountdownChip({ resetAt, accent = '#a78bfa', label = 'Reset in' }: {
    resetAt: string | Date | null | undefined;
    accent?: string;
    label?: string;
}) {
    const remaining = useCountdown(resetAt);
    return (
        <span
            title="Weekly leaderboard reset — raffle points are paid out at this moment"
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999,
                border: `1px solid ${accent}40`, background: `${accent}14`, color: accent,
                fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap',
            }}
        >
            <Clock size={11} /> {label} {remaining}
        </span>
    );
}

/** Violet raffle-points chip marking what a placement earns. */
export function PayoutBadge({ points, muted = false }: { points: number; muted?: boolean }) {
    if (!points || points <= 0) return null;
    return (
        <span
            title={`${points} raffle point${points === 1 ? '' : 's'} for this placement`}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 3, padding: '1px 7px', borderRadius: 999,
                border: '1px solid rgba(167,139,250,0.35)', background: 'rgba(139,92,246,0.14)',
                color: muted ? '#b7a8e8' : '#c4b5fd', fontSize: 10.5, fontWeight: 800, whiteSpace: 'nowrap',
            }}
        >
            <RafflePointsIcon size={11} /> +{points}
        </span>
    );
}

const PODIUM_STYLE = {
    1: { height: 78, tint: 'rgba(251,191,36,', text: '#fbbf24', label: '1st' },
    2: { height: 58, tint: 'rgba(203,213,225,', text: '#cbd5e1', label: '2nd' },
    3: { height: 58, tint: 'rgba(217,119,6,', text: '#d97706', label: '3rd' },
} as const;

function PodiumSlot({ entry, place, accent, meId, onClick, payoutForRank }: {
    entry: WeeklyBoardEntry | undefined;
    place: 1 | 2 | 3;
    accent: string;
    meId?: string | null;
    onClick?: (userId: string) => void;
    payoutForRank?: (rank: number) => number;
}) {
    const s = PODIUM_STYLE[place];
    const isMe = !!entry?.id && entry.id === meId;
    // Show the placement's raffle-point payout even on empty slots, so players
    // see what each podium spot is worth before anyone has claimed it.
    const emptyPayout = payoutForRank ? payoutForRank(place) : 0;
    return (
        <div
            onClick={() => { if (entry?.id && onClick) onClick(entry.id); }}
            style={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 4, cursor: entry?.id && onClick ? 'pointer' : 'default',
            }}
        >
            {entry ? (
                <>
                    {entry.avatarUrl ? (
                        <img src={entry.avatarUrl} alt="" style={{
                            width: place === 1 ? 40 : 32, height: place === 1 ? 40 : 32, borderRadius: '50%',
                            objectFit: 'cover', border: `2px solid ${s.tint}0.55)`,
                        }} />
                    ) : (
                        <div style={{
                            width: place === 1 ? 40 : 32, height: place === 1 ? 40 : 32, borderRadius: '50%',
                            background: `${s.tint}0.12)`, border: `2px solid ${s.tint}0.4)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <User size={place === 1 ? 18 : 14} style={{ color: s.text }} />
                        </div>
                    )}
                    <span style={{
                        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontSize: 11.5, fontWeight: 700, color: isMe ? accent : '#e2e8f0',
                    }}>{entry.username}</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: accent, lineHeight: 1 }}>{entry.elo}</span>
                    <PayoutBadge points={entry.points} />
                </>
            ) : (
                <>
                    <div style={{
                        width: place === 1 ? 40 : 32, height: place === 1 ? 40 : 32, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.1)',
                    }} />
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: '#475569' }}>—</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#334155', lineHeight: 1 }}>—</span>
                    <PayoutBadge points={emptyPayout} muted />
                </>
            )}
            <div style={{
                width: '100%', height: s.height, borderRadius: '8px 8px 0 0', marginTop: 2,
                background: `linear-gradient(to top, ${s.tint}0.04), ${s.tint}0.22))`,
                border: `1px solid ${s.tint}0.3)`, borderBottom: 'none',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                gap: 5, paddingTop: 6,
            }}>
                <span style={{ fontSize: 13, fontWeight: 900, color: s.text }}>{s.label}</span>
                {place === 1
                    ? <Trophy size={18} strokeWidth={2.25} style={{ color: s.text, filter: `drop-shadow(0 0 5px ${s.text}55)` }} />
                    : <Medal size={18} strokeWidth={2.25} style={{ color: s.text, filter: `drop-shadow(0 0 5px ${s.text}45)` }} />}
            </div>
        </div>
    );
}

/** Top-3 podium — 2nd | 1st | 3rd steps with avatar, name, ELO and payout. */
export function WeeklyPodium({ entries, accent, meId, onClickEntry, payoutForRank }: {
    entries: WeeklyBoardEntry[];
    accent: string;
    meId?: string | null;
    onClickEntry?: (userId: string) => void;
    payoutForRank?: (rank: number) => number;
}) {
    const byRank = (r: number) => entries.find(e => e.rank === r);
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 8, padding: '10px 6px 0',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
            <PodiumSlot entry={byRank(2)} place={2} accent={accent} meId={meId} onClick={onClickEntry} payoutForRank={payoutForRank} />
            <PodiumSlot entry={byRank(1)} place={1} accent={accent} meId={meId} onClick={onClickEntry} payoutForRank={payoutForRank} />
            <PodiumSlot entry={byRank(3)} place={3} accent={accent} meId={meId} onClick={onClickEntry} payoutForRank={payoutForRank} />
        </div>
    );
}

/**
 * Footer under a weekly leaderboard: a note that everyone outside the Top 10 who
 * played still earns the participation base point, plus — when the viewer is
 * outside the Top 10 — their own pinned row (rank + payout). Live-week only.
 */
export function ParticipationFooter({ participation, self, accent, onClickSelf }: {
    participation: number;
    self: { rank: number; name: string; elo: number; points: number; id?: string | null } | null;
    accent: string;
    onClickSelf?: (userId: string) => void;
}) {
    return (
        <div style={{ flexShrink: 0, marginTop: 6, paddingTop: 6, borderTop: '1px dashed rgba(255,255,255,0.12)', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {self && (
                <div
                    onClick={() => { if (self.id && onClickSelf) onClickSelf(self.id); }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '5px 8px', borderRadius: 7,
                        background: `${accent}18`, border: `1px solid ${accent}45`,
                        cursor: self.id && onClickSelf ? 'pointer' : 'default',
                    }}>
                    <span style={{ width: 26, textAlign: 'center', fontSize: 12, fontWeight: 800, color: accent }}>#{self.rank}</span>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${accent}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <User size={12} style={{ color: accent }} />
                    </div>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {self.name}<span style={{ marginLeft: 6, fontSize: 8, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7 }}>you</span>
                    </span>
                    <PayoutBadge points={self.points} />
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: accent, minWidth: 40, textAlign: 'right' }}>{self.elo}</span>
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 10, color: '#94a3b8', fontWeight: 600, textAlign: 'center' }}>
                <RafflePointsIcon size={11} />
                <span>Everyone outside the Top 10 who played earns <strong style={{ color: '#c4b5fd' }}>+{participation}</strong> for taking part.</span>
            </div>
        </div>
    );
}
